// src/components/EditProfileForm.tsx
import React, { useState, useEffect } from 'react';
import { Author, AuthorNotificationPreferences } from '../api/Author';
import apiService from '../api/apiService';
import CustomColorPicker from './CustomColorPicker'; // Import the new component
import { frenchColorPalette } from '../utils/colorPalette'; // Import the palette for name lookup
import useRecipeFormFeedback from '../hooks/forms/useRecipeFormFeedback';
import RecipeFormAlert from './recipes/RecipeFormAlert';
import { clearAuthorAvatar, replaceAuthorAvatar } from '../services/authorAvatarWorkflow';
import { Avatar, Box, Button, Card, CardBody, CardTitle, Checkbox, Divider, FileInput, Form, FormField, FormLabel, Heading, Text, TextInput } from './ui';

interface EditProfileFormProps {
  author: Author;
  token: string;
}

const EditProfileForm: React.FC<EditProfileFormProps> = ({ author, token }) => {
  const [name, setName] = useState(author.name);
  const [firstName, setFirstName] = useState(author.first_name);
  const [color, setColor] = useState(author.color || '#007bff'); // Default blue color
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [email, setEmail] = useState('');
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { message, isError, resetFeedback, setFeedback } = useRecipeFormFeedback();

  useEffect(() => {
    // Met à jour la couleur si la prop author.color change (ex: après un rechargement de la page)
    setColor(author.color || '#007bff');
  }, [author.color]);

  useEffect(() => {
    let isMounted = true;
    const loadNotificationPreferences = async () => {
      setNotificationsLoading(true);
      try {
        const prefs = await apiService.getMyNotificationPreferences();
        if (!isMounted || !prefs) return;
        setEmail(prefs.email || '');
        setNotifyEmail(!!prefs.notify_email_enabled);
      } catch (error: any) {
        if (!isMounted) return;
        setFeedback(`Impossible de charger les préférences de notification: ${error.message}`, true);
      } finally {
        if (isMounted) {
          setNotificationsLoading(false);
        }
      }
    };

    loadNotificationPreferences();
    return () => {
      isMounted = false;
    };
  }, [setFeedback]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    resetFeedback();

    try {
      let avatarUrl = author.avatar_url;

      if (avatarFile) {
        setFeedback('Redimensionnement et téléversement de l\'avatar...');
        avatarUrl = await replaceAuthorAvatar(avatarFile, author.avatar_url);
      }

      const updatedAuthorData: Partial<Author> = {
        id: author.id,
        name,
        first_name: firstName,
        avatar_url: avatarUrl,
        color,
      };

      if (notifyEmail && !email.trim()) {
        throw new Error("Veuillez renseigner un e-mail pour activer les notifications e-mail.");
      }

      await apiService.updateAuthor(updatedAuthorData);

      const updatedNotificationPreferences: AuthorNotificationPreferences = {
        email: email.trim() || null,
        notify_email_enabled: notifyEmail,
      };
      await apiService.updateMyNotificationPreferences(updatedNotificationPreferences);

      setFeedback('Profil mis à jour avec succès !');
      window.location.reload();

    } catch (error: any) {
      setFeedback(`Erreur lors de la mise à jour : ${error.message}`, true);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleDeleteAvatar = async () => {
    if (!author.avatar_url || !window.confirm("Voulez-vous vraiment supprimer votre avatar ?")) return;

    setSubmitting(true);
    resetFeedback();

    try {
      await clearAuthorAvatar(author.id, author.avatar_url);
      setAvatarFile(null);
      setFeedback('Avatar supprimé avec succès !');
      window.location.reload();

    } catch (error: any) {
      setFeedback(`Erreur lors de la suppression de l'avatar : ${error.message}`, true);
    } finally {
      setSubmitting(false);
    }
  };

  // Helper to find the name of the selected color
  const getSelectedColorName = (hex: string): string => {
    const foundColor = frenchColorPalette.find(c => c.hex.toLowerCase() === hex.toLowerCase());
    return foundColor ? foundColor.name : hex;
  };

  return (
    <Card shadow="sm">
      <CardBody paddingBottom={4}>
        <CardTitle as="h2" textAlign="center" marginBottom={4}>Modifier mon profil</CardTitle>
        <Box textAlign="center" marginBottom={4}>
          <Avatar
            src={author.avatar_url || 'https://via.placeholder.com/150'} 
            alt="Avatar" 
            size={150}
            style={{ border: `4px solid ${color || '#cccccc'}` }}
          />
        </Box>
        <Form onSubmit={handleSubmit}>
          <FormField label="Prénom" htmlFor="firstName">
            <TextInput
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </FormField>
          <FormField label="Nom" htmlFor="name">
            <TextInput
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </FormField>
          <FormField label="Changer l'avatar" htmlFor="avatar">
            <FileInput
              id="avatar"
              accept="image/*"
              onChange={(e) => setAvatarFile(e.target.files ? e.target.files[0] : null)}
            />
            {author.avatar_url && (
              <Button
                type="button"
                variant="danger"
                outline
                size="sm"
                marginTop={2}
                onClick={handleDeleteAvatar}
                disabled={submitting}
              >
                Supprimer l'avatar
              </Button>
            )}
          </FormField>
          <Box marginBottom={3}>
            <FormLabel>
              Couleur de l'étiquette : <Text as="span" fontWeight="bold">{getSelectedColorName(color)}</Text>
            </FormLabel>
            <CustomColorPicker value={color} onChange={setColor} />
          </Box>

          <Divider marginY={4} />
          <Heading as="h5" marginBottom={3}>Notifications</Heading>
          {notificationsLoading ? (
            <Text as="div" tone="muted" marginBottom={3}>Chargement des préférences de notification...</Text>
          ) : (
            <>
              <Box marginBottom={3}>
                <FormLabel htmlFor="notificationEmail">E-mail de notification</FormLabel>
                <TextInput
                  type="email"
                  id="notificationEmail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ex: moi@exemple.com"
                />
              </Box>

              <Checkbox
                id="notifyEmailEnabled"
                label="Activer les notifications par e-mail"
                marginBottom={2}
                checked={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.checked)}
              />
            </>
          )}
          
          <Button type="submit" variant="primary" fullWidth marginBottom={3} disabled={submitting}>
            {submitting ? 'Mise à jour en cours...' : 'Mettre à jour le profil'}
          </Button>

          <RecipeFormAlert message={message} isError={isError} />
        </Form>
      </CardBody>
    </Card>
  );
};

export default EditProfileForm;
